import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import {
  ArrowsClockwise,
  ArrowsOut,
  ListBullets,
  Bell,
  Question,
  UploadSimple,
} from "phosphor-react";
import _ from "lodash";
import html2canvas from "html2canvas";

//components
import AppHoc from "../AppHOC";
import TaskWidget from "./TaskWidget";
import BellWidget from "./BellWidget";
import UserProfile from "./UserProfile";
import HelpWidget from "./HelpWidget";
import HelpModal from "./HelpModal";
import HelpDrawer from "./HelpDrawer";
import SiteList from "../SitesDropdownWidget/SiteList";
import ApplicationsMasterTable from "../../views/overview/AppView/ApplicationsMasterTable";

//utils
import {
  isDefaultHeader,
  isAccountsPage,
  setLocalStorageFlag,
  refresh,
  setCurrentPath,
  getLocalStorageFlag,
  removeLocalStorageFlag,
  removeItemsLocalStorage,
  getPostLoginNotifications,
  isOverviewPage,
  getStoredPath,
  getStoredTimePeriod,
  isTrafficLogsPage,
  getCurrentPathName,
  getTitleLabel,
  setPageTitle,
  formatPathName,
  getSubTitleLabel,
} from "../../utils/common";
import { getCookie } from "../../serviceWorker";
import { initDatadogRum } from "../../monitoring";
import { getTimePeriodELT } from "../../utils/displayTime";
import { BASE_URL, LICENSE_PAGE_URL } from "../../apis/apiConstants";
import { useMount } from "../../utils/genericCommon";
import { useI18n } from "../../i18n/useI18n";
import UnifyMFEWidgetRenderer from "../mfe/UnifyMFEWidgetRenderer";
import AIAssistantUnavailable from "./AIAssistantUnavailable";
import {
  C3_CUI_DATA_STATUS,
  initializeC3CuiDataBootstrap,
} from "./c3CuiDataBootstrap";

//config
import apiService from "../../config/api-config";
import timeFilterConfig from "../config/time-filter-config";
import { countNotficationPayload } from "./config";

//style
import css from "../genericMagneticStyle.less";
import { deepLink } from "../../utils/deepLink";
import { routeURLs } from "../../utils/enums";
import { getVManageMonitorUrl } from "../../utils/vManageNavigation";

const CommonSitesTable = lazy(
  () => import("../../views/overview/CommonSitesTable"),
);
const MasterTable = lazy(
  () => import("../../views/circuits/common/MasterTable"),
);

let username = "admin";
let pathToAccounts = routeURLs.accounts;
const vMangeRedirectURI = getCookie("cl-vManageURI");
const isUIconvergence = getCookie("cl-analytics");
const AI_ASSISTANT_FEATURE = "AI Assistant";
const AI_ASSISTANT_ROLES = new Set([
  "AI Assistant-read",
  "AI Assistant-write"
]);
const bannerStyle = { marginLeft: "150px" };

const hasAIAssistantRole = response =>
  Array.isArray(response?.data?.data) &&
  response.data.data.some(item => AI_ASSISTANT_ROLES.has(item?.role));

const [
  HbrIcon,
  HbrBanner,
  HbrButton,
  HbrBadge,
  HbrTooltip,
  HbrShellHeader,
  HbrMenuDivider,
] = reactWrapper([
  "hbr-icon",
  "hbr-banner",
  "hbr-button",
  "hbr-badge",
  "hbr-tooltip",
  "hbr-shell-header",
  "hbr-menu-divider",
]);

const getInitialC3HeaderStatus = () => {
  if (!isUIconvergence) {
    return C3_CUI_DATA_STATUS.INACTIVE;
  }

  return initializeC3CuiDataBootstrap().status;
};

const AppHeader = (props) => {
  const { tGenerics } = useI18n("generics");
  const {
    token,
    urlPrefix,
    globalFilter,
    setOverlays,
    setAccounts,
    errorHeader,
    setSupportRole,
    setCtrlTimeFilter,
    setSsoLogoutCookie,
    setSelectedOverlay,
    setSelectedOverlayRoles,
    setPostLoginNotifications,
    setSelectedSite,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const loggedInUser = getLocalStorageFlag("user_email");
  const { notifications, timeFilter } = globalFilter;
  const position = urlPrefix
    ? location.pathname.indexOf(urlPrefix) + urlPrefix.length + 1
    : 0;
  const urlAry = location?.state?.isInValid
    ? []
    : location.pathname.substring(position).split("/");
  const [c3HeaderStatus] = useState(getInitialC3HeaderStatus);
  const [hasConvergedAIAssistantAccess, setHasConvergedAIAssistantAccess] =
    useState(false);
  const showC3Header = Boolean(
    isUIconvergence && c3HeaderStatus === C3_CUI_DATA_STATUS.READY,
  );
  const isAnchorReady = window._isAnchorLoaded === true;
  const hasAIAssistantAccess =
    !isUIconvergence || hasConvergedAIAssistantAccess;
  const shouldShowAIAssistant =
    hasAIAssistantAccess && !isAccountsPage();
  const shouldShowAICanvasLauncher = isAnchorReady && showC3Header;

  const appShellHeaderClassName =
    "va-app-shell-header va-app-shell-header--light";
  const appHeaderClassName = `${css["app-header"]} c3-theme-light`;

  const waffleRef = useRef({ isWaffleShown: false });
  const bannerLicenceRef = useRef({ isShown: false });
  const notificationCountRef = useRef({
    isShown: false,
    isError: false,
    styleWrapper: { width: "3px" },
  });
  const taskCountRef = useRef({ isShown: false, isError: false });
  const [headerIconState, setHeaderIconState] = useState({});
  const mount = useMount();
  const [skipLinkFocus, setSkipLinkFocus] = useState(false);

  useEffect(() => {
    if (!isUIconvergence) return undefined;

    let isActive = true;
    const checkAIAssistantAccess = async () => {
      const userGroupResponse = await apiService.getvManageUserGroups();

      if (!isActive) return;
      if (!userGroupResponse || userGroupResponse.errorObject instanceof Object) {
        setHasConvergedAIAssistantAccess(false);
        return;
      }

      const userGroupPayload = userGroupResponse.data;
      const userGroupResponseText = typeof userGroupPayload === "string"
        ? userGroupPayload
        : JSON.stringify(userGroupPayload ?? "");

      if (!userGroupResponseText.includes(AI_ASSISTANT_FEATURE)) {
        setHasConvergedAIAssistantAccess(true);
        return;
      }

      const userRolesResponse = await apiService.getvManageUserRoles();

      if (!isActive) return;
      const hasAllowedRole =
        userRolesResponse &&
        !(userRolesResponse.errorObject instanceof Object) &&
        hasAIAssistantRole(userRolesResponse);

      setHasConvergedAIAssistantAccess(Boolean(hasAllowedRole));
    };

    checkAIAssistantAccess();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    // Handle initial load
    const handleHashChange = deepLink(
      props,
      BASE_URL,
      navigate,
      isUIconvergence,
    );

    // Cleanup the event listener
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [navigate]);

  useEffect(() => {
    if (location.hash.length > 0) {
      const hashPathName = location.hash.replace("#", "");
      const formattedPathName = hashPathName
        ? formatPathName(hashPathName, true)
        : "";
      const pageTitle = getTitleLabel(formattedPathName);
      setPageTitle(pageTitle);
    } else {
      const pathName = getCurrentPathName(location);
      const formattedPathName = pathName ? formatPathName(pathName) : "";
      if (Array.isArray(formattedPathName) && formattedPathName.length > 0) {
        const [main, ...subs] = formattedPathName;
        const pageTitle = getTitleLabel(main);
        const subTitles = subs
          .map((s, i) => getSubTitleLabel(s, formattedPathName[i]))
          .filter(Boolean)
          .join(" / ");
        setPageTitle(pageTitle, subTitles);
      } else {
        const pageTitle = getTitleLabel(formattedPathName);
        setPageTitle(pageTitle);
      }
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const runEffect = async () => {
      setCurrentPath();
      if (!errorHeader) {
        let overlays = { data: [] };
        let updatedOverlay = [];
        let accounts = [];

        //Get overlays
        try {
          const envVar = process.env;
          if (
            _.isEmpty(envVar.REACT_APP_SKIP_RBAC_DISABLE_RBAC) ||
            envVar.REACT_APP_SKIP_RBAC_DISABLE_RBAC === "FALSE"
          ) {
            const currentOverlay = getLocalStorageFlag("currentOverlay");

            if (
              isAccountsPage() ||
              !currentOverlay ||
              !getLocalStorageFlag("overlays")
            ) {
              let activeFabrics = [];

              removeItemsLocalStorage();

              overlays = await apiService.getSmartAccountData(token);

              //get the list of active/activated overlays
              overlays.data.data.map((d) => {
                d.overlays.map((data) => {
                  if (data.overlay_id) {
                    activeFabrics = [
                      ...activeFabrics,
                      {
                        status: data.status,
                        overlay_id: data.overlay_id,
                      },
                    ];
                  }
                });
              });

              /**
               * check if cross launch
               * or check overlays  array is a single active/activated fabric
               */
              if (
                activeFabrics.length === 1 &&
                (activeFabrics[0].status === "ACTIVATED" ||
                  activeFabrics[0].status === "ACTIVE")
              ) {
                const currentOverlay = activeFabrics[0].overlay_id;
                setLocalStorageFlag("currentOverlay", currentOverlay);
                username = getLocalStorageFlag("user_email");
              }
            }

            //default vA, not UI convergence
            if (getLocalStorageFlag("isWaffleShown")) {
              waffleRef.current.isWaffleShown = true;
            }

            if (overlays !== undefined) {
              //when smart account is called first time set overlay data in reducer
              if (!overlays.data.data) {
                overlays = JSON.parse(getLocalStorageFlag("overlays"));
              } else {
                setLocalStorageFlag("overlays", JSON.stringify(overlays));
              }

              username = overlays.data.email;
              setLocalStorageFlag("user_email", username);
              accounts = [...accounts, ...overlays.data.data];

              overlays.data.data.map((d) => {
                d.overlays.map((data) => {
                  if (data.overlay_id) {
                    updatedOverlay = [
                      ...updatedOverlay,
                      {
                        key: data.overlay_id,
                        value: data.org_name,
                        status: data.status,
                        roles: data.roles,
                      },
                    ];
                  }
                });

                updatedOverlay?.sort((a, b) =>
                  a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1,
                );
              });

              if (overlays.data.enable_rum) {
                setLocalStorageFlag("enable_rum", overlays.data.enable_rum);
                initDatadogRum(overlays.data.rum_config);
              }
              if (overlays.data.is_support_role) {
                setLocalStorageFlag(
                  "is_support_role",
                  overlays.data.is_support_role,
                );
                setSupportRole(true);
              }
              setOverlays(updatedOverlay);
              setAccounts(accounts);

              // Caching implementation for selected overlay
              const cachedOverlay = localStorage.getItem("currentOverlay");
              const cachedOverlayName =
                localStorage.getItem("currentOverlayName");
              const roles = JSON.parse(localStorage.getItem("roles"));
              const launchDashboard = getStoredPath();

              switch (true) {
                case updatedOverlay.length === 1 &&
                  (updatedOverlay[0].status === "ACTIVATED" ||
                    updatedOverlay[0].status === "ACTIVE"): {
                  const currOverlayKey = updatedOverlay[0].key;
                  const currOverlayName = updatedOverlay[0].value;

                  setLocalStorageFlag("currentOverlay", currOverlayKey);
                  setLocalStorageFlag("currentOverlayName", currOverlayName);

                  setSelectedOverlay(
                    localStorage.getItem("currentOverlay") || currOverlayKey,
                    localStorage.getItem("currentOverlayName") ||
                      currOverlayName,
                  );

                  setSelectedOverlayRoles(updatedOverlay[0].roles);

                  if (!isUIconvergence) {
                    if (vMangeRedirectURI && launchDashboard) {
                      navigate(`${BASE_URL}/${launchDashboard}`);
                    } else {
                      navigate(routeURLs.overview);
                    }
                  }

                  break;
                }
                case cachedOverlay && !_.isEmpty(roles) && !vMangeRedirectURI: {
                  setSelectedOverlay(cachedOverlay, cachedOverlayName);
                  setSelectedOverlayRoles(roles);
                  if (vMangeRedirectURI && launchDashboard) {
                    navigate(`${BASE_URL}/${launchDashboard}`);
                  } else if (
                    vMangeRedirectURI ||
                    window.location.pathname === routeURLs.overview
                  ) {
                    navigate(routeURLs.overview);
                  }
                  break;
                }
                default: {
                  if (!window.location.href.includes(pathToAccounts)) {
                    navigate(pathToAccounts);
                  }
                  break;
                }
              }
            }
          }
          //To avoid rendering on accounts page
          // and do not call table availability api when user is not admin in /reports page
          if (
            localStorage.getItem("currentOverlay") &&
            !location.pathname.includes("/reports")
          ) {
            getPostLoginNotifications(setPostLoginNotifications);
          }
        } catch (e) {
          console.log("error while calling api overlay", e);
        }
        setLocalStorageFlag("PathAnalyticsType", "PA");
      }

      // check for cross Launch and store logout cookie for seesion Timeout case.
      const logoutCookie = getCookie("lg-vManageURI");
      if (logoutCookie) {
        setSsoLogoutCookie(logoutCookie);
        sessionStorage.setItem("cl-logout", logoutCookie);
      }
    };

    runEffect();
  }, []);

  useEffect(() => {
    const runEffect = async () => {
      //convergence api will be called in that block below
      if (
        getLocalStorageFlag("xsrfToken") &&
        mount.initMount &&
        isUIconvergence &&
        !notificationCountRef.current.isShown
      ) {
        mount.initMount = false;

        getNotificationCountFromVManage();
        getTaskCountFromVManage();

        //get the state of waffle menu in the top right corner
        const waffleMenuResponse = await apiService.getWaffleMenu();
        const compliantResponse = await apiService.getCompliance();

        if (compliantResponse.errorObject instanceof Object === false) {
          if (compliantResponse.data.data.status === "Out Of Compliance") {
            setLocalStorageFlag("isCompliant", true);
            bannerLicenceRef.current.isShown = true;
          }
        } else {
          removeLocalStorageFlag("isCompliant");
          bannerLicenceRef.current.isShown = false;
        }

        //waffleEnabled can be either true or false
        if (
          waffleMenuResponse.errorObject instanceof Object === true ||
          waffleMenuResponse?.data?.data[0]?.waffleEnabled === false
        ) {
          waffleRef.current.isWaffleShown = false;
          removeLocalStorageFlag("isWaffleShown", false);

          //else if  error or waffle is disabled than isWaffleShown var stays false
        } else if (waffleMenuResponse?.data?.data[0]?.waffleEnabled) {
          waffleRef.current.isWaffleShown = true;
          setLocalStorageFlag("isWaffleShown", true);
        }
      }

      const div = document.querySelector(".headerApp");
      if (div) {
        div.style.paddingBottom = "0rem";
        div.style.removeProperty("background");
      }

      if (
        getLocalStorageFlag("currentOverlay") &&
        globalFilter.selectedOverlay !==
          getLocalStorageFlag("currentOverlay") &&
        isOverviewPage()
      ) {
        // get info about selected overlay
        setSelectedOverlay(
          localStorage.getItem("currentOverlay"),
          localStorage.getItem("currentOverlayName"),
        );

        let defaultTimePeriodSelected = getStoredTimePeriod();
        let time = timeFilterConfig[defaultTimePeriodSelected].value;

        setSelectedOverlayRoles(JSON.parse(getLocalStorageFlag("roles")));
        const timePeriod = getTimePeriodELT(time);

        setCtrlTimeFilter(timePeriod, time);
      }
    };

    runEffect();
  }, [timeFilter, location.pathname]);

  const getNotificationCountFromVManage = async () => {
    const notificationVMResponse = await apiService.getAlarmCount(
      countNotficationPayload,
    );

    //check if notification is successful
    if (notificationVMResponse.errorObject instanceof Object) {
      notificationCountRef.current.isError = true;
    } else {
      const count = notificationVMResponse.data.data[0].count;
      //check if notification needs to be shown next to bell icon
      if (count > 0) notificationCountRef.current.isShown = true;

      //case one is when there are more than 99 notification
      //in that case we show 99+
      if (count > 99) {
        notificationCountRef.current.count = "99+";
        notificationCountRef.current.styleWrapper = { width: "8px" };
      } else if (count > 9) {
        //case 2 the count is between 99 and 9 we need to adjust the width of notification
        notificationCountRef.current.count = count;
        notificationCountRef.current.styleWrapper = { width: "3px" };
      } else {
        //case 3 the count is less than 10 we need to adjust the width of notification
        notificationCountRef.current.count = count;
        notificationCountRef.current.styleWrapper = { width: "1px" };
      }
    }
  };

  const getTaskCountFromVManage = async () => {
    const taskVMResponse = await apiService.getTaskSummary();

    //check if notification is successful
    if (taskVMResponse.errorObject instanceof Object) {
      taskCountRef.current.isError = true;
    } else {
      const count = taskVMResponse.data.data.filter(
        (d) => d.status !== "done",
      ).length;

      if (count > 0) taskCountRef.current.isShown = true;

      taskCountRef.current.count = count >= 99 ? "99+" : count;
    }
  };

  const toggleFullscreen = () => {
    const elem = document.getElementById("full-screen-content");
    elem.style.overflow = "auto";
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullScreen) {
      elem.webkitRequestFullScreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.msRequestFullScreen) {
      elem.msRequestFullScreen();
    }
  };

  const captureScreen = async () => {
    const elem = document.getElementById("main-content");

    const img = await html2canvas(elem, {
      width: elem.scrollWidth,
      height: elem.scrollHeight,
      useCORS: true,
      logging: false,
    });
    const contentDataURL = img.toDataURL("image/png");
    // window.location.href = contentDataURL.replace('image/png', 'image/octet-stream');
    const downloadLink = document.createElement("a");
    downloadLink.href = contentDataURL;
    downloadLink.style.display = "none";
    const path = window.location.pathname.split("/").slice(-1)[0];
    downloadLink.setAttribute("download", `${path}.png`);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const onRefresh = async (e) => {
    e.preventDefault();
    refresh(props, location.pathname);
    if (localStorage.getItem("currentOverlay")) {
      //To avoid rendering on accounts page
      getPostLoginNotifications(setPostLoginNotifications);
    }

    //reload the page
    if (isTrafficLogsPage(location.pathname)) {
      window.location.reload();
    }
  };

  const openBell = () => {
    setHeaderIconState({ bell: true });
  };

  const closeBell = () => {
    setHeaderIconState({ bell: false });
  };

  const openHelpModal = (isOpen) => {
    setHeaderIconState({ helpModal: isOpen });
  };

  const openHelpDrawer = (isOpen) => {
    setHeaderIconState({ helpDrawer: isOpen });
  };

  //On click of notification close icon
  const onClosed = async (e, uuid) => {
    let uuidArr = [];
    uuidArr.push(uuid);
    let payload = {
      notification_uuid: uuidArr,
    };
    let notificationStatus = await apiService.updateNotifications(payload);
    if (notificationStatus?.status === 200) {
      setPostLoginNotifications([]);
    }
    // Reset shell menu offset when notification banner is closed
    const shellMenu = document.querySelector(".shell-menu");
    if (shellMenu) {
      shellMenu.style.removeProperty("--shell-menu-content-offset");
    }
  };

  //Method to render post-login notifications
  const renderNotification = () => {
    const { notifications } = globalFilter;
    return notifications.map((item, key) => {
      if (key === 0) {
        //To render only one notification
        const iconMapping = {
          info: "alerts-info-large",
          warning: "alerts-warning-large",
          large: "status-negative",
        };

        // Set a default fallback icon
        const iconName =
          iconMapping[item.notification_criticality.icon] ||
          "alerts-info-large";

        return (
          <div className="notification-banner" key={key}>
            <HbrBanner
              sentiment={item.notification_criticality}
              variant="fill"
              open={true}
              closable={item.notification_is_dismissiable}
              product={true}
              data-testid={`single-${item.notification_criticality}`}
              key={key.toString()}
              onHbr-hide={(e) => onClosed(e, item.notification_uuid)}
            >
              <HbrIcon slot="icon" name={iconName} />
              <div className="hbr-css__layout-col-2xs">
                <div
                  dangerouslySetInnerHTML={{ __html: item.notification_msg }}
                ></div>
              </div>
            </HbrBanner>
          </div>
        );
      }
    });
  };

  // on task icon click
  const openTask = () => {
    setHeaderIconState({ task: true });
  };

  const closeTask = () => {
    setHeaderIconState({ task: false });
  };

  const onHeaderBrandingClick = async (e) => {
    e.preventDefault();
    const clVmanageURI = getCookie("cl-analytics");
    if (clVmanageURI) {
      window.location.href = getVManageMonitorUrl("/overview");
    } else if (!matchPath({ path: routeURLs.overview }, location.pathname)) {
      navigate(routeURLs.overview);
    }
  };

  const handleClick = () => {
    window.location.href = LICENSE_PAGE_URL;
  };

  const Brand = (
    <a
      slot="logo-name"
      className={isUIconvergence ? "no-header-branding" : "header-branding"}
      onClick={(e) => onHeaderBrandingClick(e)}
      role="link"
    >
      <hbr-icon
        name="cisco"
        label={tGenerics("a11yLabels.logoLabel")}
      ></hbr-icon>
      <div className="product-name" data-testid="product-name">
        {tGenerics("productLabel")}
      </div>
    </a>
  );

  return (
    <>
      <div
        id="skiplinks"
        onFocus={(e) => {
          e.preventDefault();
          setSkipLinkFocus(true);
        }}
        onBlur={() => setSkipLinkFocus(false)}
      >
        <a
          href="#main-content"
          className={skipLinkFocus ? css["skip-link-focus"] : css["skip-link"]}
        >
          <span>Skip to main content</span>
        </a>
      </div>
      <div className={appHeaderClassName}>
        {getLocalStorageFlag("overlays") && (
          <>
            <Suspense fallback={null}>
              <CommonSitesTable data={props} />
            </Suspense>
            <Suspense fallback={null}>
              <MasterTable />
            </Suspense>
            <ApplicationsMasterTable />
            <SiteList />
          </>
        )}

        {headerIconState?.task && <TaskWidget closeTask={closeTask} />}
        {headerIconState?.bell && (
          <BellWidget closeBell={closeBell} setSelectedSite={setSelectedSite} />
        )}
        {(!isDefaultHeader(location) && urlAry[0] !== "") ||
        (errorHeader && getCookie("okta-oauth-state")) ? (
          <HbrShellHeader className={appShellHeaderClassName}>
            {!showC3Header && Brand}
            {showC3Header && (
              <div slot="logo-name" id="unify-mfe-header">
                <UnifyMFEWidgetRenderer componentId="cisco.sdwan.app.c3Header/c3Header" />
              </div>
            )}            
            <div slot="utilities" id="header-utilities">
              {/* Global Search widget */}
              {isUIconvergence && window._isAnchorLoaded === true && (
                <div id="unify-mfe-global-search">
                  <UnifyMFEWidgetRenderer
                    componentId="cisco.sdwan.app.globalSearch/SdwanGlobalSearchComponent"
                    errorComponent={<div id="sdwanGlobalSearchComponent-error"></div>}
                  />
                </div>
              )}
              <div id="header-icons">
                {/* AI Assistant widget */}
                {shouldShowAIAssistant && (
                  <div id="unify-mfe-aiassist">
                    <UnifyMFEWidgetRenderer componentId="cisco.sdwan.app.header/aiAssistant"
                      errorComponent={<AIAssistantUnavailable />}
                    />
                  </div>
                )}
                {/* AI Canvas launcher widget */}
                {shouldShowAICanvasLauncher && (
                  <div id="unify-mfe-ai-canvas">
                    <UnifyMFEWidgetRenderer
                      componentId="cisco.sdwan.app.c3Header/aiCanvasLauncher"
                      errorComponent={<div id="aiCanvasLauncher-error"></div>}
                    />
                  </div>
                )}
                {/* Header icons for non converged ui*/}
                {!isUIconvergence &&
                  <>
                    <HbrTooltip
                      id="tooltip-refresh"
                      data-testid="tooltip-refresh"
                      content={tGenerics("refresh")}
                      placement="top"
                    >
                      <HbrButton
                        onClick={(e) => onRefresh(e)}
                        data-testid="refresh-button"
                        noPadding
                        sentiment="header"
                      >
                        <ArrowsClockwise size={22} weight="bold" />
                      </HbrButton>
                    </HbrTooltip>
                    <HbrTooltip
                      id="tooltip-fullscreen"
                      data-testid="tooltip-fullscreen"
                      content={tGenerics("fullScreen")}
                      placement="top"
                    >
                      <HbrButton
                        onClick={toggleFullscreen}
                        noPadding
                        data-testid="fullscreen-button"
                        sentiment="header"
                      >
                        <ArrowsOut size={22} weight="bold" />
                      </HbrButton>
                    </HbrTooltip>
                    <HbrButton
                      onClick={captureScreen}
                      noPadding
                      data-testid="tooltip-download"
                      sentiment="header"
                      slot="menu"
                      aria={{ label: tGenerics("a11yLabels.uploadButton") }}
                    >
                      <UploadSimple size={22} weight="bold" />
                    </HbrButton>
                    <HbrButton
                      noPadding
                      sentiment="header"
                      slot="menu"
                      data-testid="tooltip-help"
                      disabled
                      aria={{ label: tGenerics("a11yLabels.helpButton") }}
                    >
                      <Question size={22} weight="bold" />
                    </HbrButton>
                  </>
                }
                {/* Header icons for converged ui*/}
                {isUIconvergence && (
                  <>
                    <HbrButton
                      onClick={openTask}
                      sentiment="header"
                      slot="menu"
                      size="small"
                    >
                      <ListBullets size={24} weight="bold" />
                      {taskCountRef.current.isShown &&
                        !taskCountRef.current.isError && (
                          <span className="notification-badge-wrapper">
                            <HbrBadge
                              pill
                              inline
                              size="small"
                              sentiment="danger"
                              className="notification-badge"
                            >
                              <div id="notification-tag">
                                {taskCountRef.current.count}
                              </div>
                            </HbrBadge>
                          </span>
                        )}
                    </HbrButton>
                    <HelpWidget
                      openHelpModal={openHelpModal}
                      openHelpDrawer={openHelpDrawer}
                    />
                    <HbrButton
                      onClick={openBell}
                      noPadding
                      sentiment="header"
                      slot="menu"
                    >
                      <Bell size={24} weight="bold" />
                      {notificationCountRef.current.isShown &&
                        !notificationCountRef.current.isError && (
                          <span
                            className="notification-badge-wrapper"
                            style={notificationCountRef.current.styleWrapper}
                          >
                            <HbrBadge
                              pill
                              inline
                              size="small"
                              sentiment="danger"
                              className="notification-badge"
                            >
                              <div id="notification-tag">
                                {notificationCountRef.current.count}
                              </div>
                            </HbrBadge>
                          </span>
                        )}
                    </HbrButton>
                  </>
                )}
                <HbrMenuDivider vertical sentiment="header"></HbrMenuDivider>
                {loggedInUser && (
                  <UserProfile
                    loggedInUser={loggedInUser}
                    waffleRef={waffleRef}
                  />
                )}
              </div>
            </div>
          </HbrShellHeader>
        ) : (
          // END: Harbor shell header
          ""
        )}

        {notifications.length && localStorage.getItem("currentOverlay")
          ? renderNotification()
          : null}

        {bannerLicenceRef.current.isShown === "true" && (
          <div className={css["compliance"]}>
            <div className="outer-banner">
              <div className="banner" style={bannerStyle}>
                <HbrBanner sentiment="danger" variant="fill" open>
                  <HbrIcon slot="icon" name="alerts-negative-small"></HbrIcon>
                  <span className="banner-text">
                    {tGenerics("complianceBannerText")}
                    <HbrButton variant="text" onClick={handleClick}>
                      <span id="click-here">
                        {tGenerics("complianceBannerClickHere")}
                      </span>
                    </HbrButton>
                    {tGenerics("complianceBannerActions")}
                  </span>
                </HbrBanner>
              </div>
            </div>
          </div>
        )}
        {headerIconState.helpModal && (
          <HelpModal openHelpModal={openHelpModal} {...props} />
        )}
        {headerIconState.helpDrawer && (
          <HelpDrawer openHelpDrawer={openHelpDrawer} />
        )}
      </div>
    </>
  );
};

AppHeader.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  setSelectedOverlay: PropTypes.func.isRequired,
  setSsoLogoutCookie: PropTypes.func.isRequired,
  setOverlays: PropTypes.func.isRequired,
  urlPrefix: PropTypes.string.isRequired,
  setSelectedOverlayRoles: PropTypes.func.isRequired,
  setAccounts: PropTypes.func.isRequired,
  token: PropTypes.string,
  errorHeader: PropTypes.string,
  setSupportRole: PropTypes.func.isRequired,
  setAltoProps: PropTypes.func,
  setCtrlTimeFilter: PropTypes.func,
  setPostLoginNotifications: PropTypes.func,
  setAnomalyRefresh: PropTypes.func.isRequired,
  closeTask: PropTypes.func,
  setSelectedSite: PropTypes.func,
  icon: PropTypes.object,
};

export default AppHoc(AppHeader);
